import { FormInput, toast } from '@systemeio/ui'
import { useSaveEmail } from 'modules/profile/user-settings/hooks/use-save-email'
import { UseUserSettingsInterface } from 'modules/profile/user-settings/types/user-settings-interface'
import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import { Loader } from 'shared/components/loader'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { isValidEmail } from 'shared/utils/email-validation'
import { KeyedMutator } from 'swr'
import { CheckIcon, CloseIcon, PenIcon } from '@systemeio/ui/icons'

interface UserSettingsEmailProps {
  email?: string
  mutate: KeyedMutator<UseUserSettingsInterface>
}

const ICON_BASE_CLASS_NAME = 'w-5 h-5 main-transition-colors'

const UserSettingsEmail: FC<PropsWithChildren<UserSettingsEmailProps>> = ({
  email = '',
  mutate,
  children,
}) => {
  const { t } = useLocoTranslation()
  const { checkImpersonatorAdminRoles, isSubAccountImpersonation } = useUser()
  const canManage =
    !isSubAccountImpersonation &&
    checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin])

  const [isEditing, setIsEditing] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const [newEmail, setNewEmail] = useState(email)

  const { saveEmail } = useSaveEmail()

  useEffect(() => {
    setNewEmail(email)
  }, [email])

  const onSave = async () => {
    try {
      if (!isValidEmail(newEmail)) {
        return toast.error(t('dashboard.validation.email_not_valid'))
      }
      setIsFetching(true)
      await saveEmail(newEmail)
      await mutate(data => {
        if (data && data.user) {
          return { ...data, user: { ...data.user, email: newEmail } }
        }
      }, false)
      toast.success(t('global.changes_saved'))
      setIsEditing(false)
    } catch {
    } finally {
      setIsFetching(false)
    }
  }

  const isSameEmail = newEmail === email

  const isEmailEmpty = newEmail === ''

  return (
    <div className="bg-brand-primary-subtle p-5">
      {!(isSubAccountImpersonation && !email) && (
        <div className={'text-text-primary flex h-[42px] items-center gap-1.5 text-base font-bold'}>
          <span>{`${t('global.email')}:`}</span>
          {isEditing ? (
            <FormInput
              inputClassName={'w-64'}
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              disabled={isFetching}
              statusIcon={isFetching && <Loader small={true} />}
            />
          ) : (
            <span>{email}</span>
          )}
          {isEditing && (
            <button
              onClick={onSave}
              disabled={isFetching || isSameEmail || isEmailEmpty}
              className={'group outline-hidden '}
            >
              <CheckIcon
                className={`${ICON_BASE_CLASS_NAME} stroke-fill-primary ${
                  !(isFetching || isSameEmail || isEmailEmpty) &&
                  'group-hover:stroke-green group-focus-visible:stroke-green'
                }`}
              />
            </button>
          )}
          {canManage && (
            <button
              disabled={isFetching}
              onClick={() => {
                if (isEditing) {
                  setNewEmail(email)
                }
                setIsEditing(!isEditing)
              }}
              className={'group outline-hidden '}
            >
              {isEditing ? (
                <CloseIcon
                  className={`${ICON_BASE_CLASS_NAME} fill-fill-primary ${
                    !isFetching && 'group-hover:fill-danger group-focus-visible:fill-danger'
                  }`}
                />
              ) : (
                <PenIcon
                  className={`${ICON_BASE_CLASS_NAME} group-hover:fill-brand-primary group-focus-visible:fill-brand-primary`}
                />
              )}
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

export default UserSettingsEmail
